import { Component, OnInit, Input, Output, ElementRef, EventEmitter, ViewEncapsulation, HostBinding } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { CfIconComponent } from '../icon/icon.component';
import { IconModel } from '../../models/icon/icon.model';
import { ButtonModel } from '../../models/button/button.model';
import { ButtonStylingModel } from '../../models/button/button-styling.model';
import { ButtonTemplates } from '../../templates/button.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Button Component</p>
 * <pre><code>
 * import {{'{'}} ButtonModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} ButtonStylingModel {{'}'}} from 'cedrus-fusion'
 * <</code>cf-button<code>></code><code><</code><code>/</code>cf-button<code>>
 * </code></pre>
 */
export class CfButtonComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden
 */
notifacationClickable = '';
/**
 * <pre>{{'{'}}
 *  display: boolean,         // True or False. Default: True
 *  disable: boolean,         // True or False. Default: False
 *  label: string,            // Default: CF-BUTTON
 *  iconProperty: IconModel,  // Refer to Icon Component
 *  iconPosition: string,     // left, right .Default: left
 *  waiting: {{'{'}}
 *   value: boolean,         // Display waiting mode (Icon)
 *   disable: boolean,       // True or False
 *   iconProperty: IconModel // Refer to Icon Component
 *  {{'}'}}
 * {{'}'}}
 * </pre>
 * <p>Example</p>
 * <pre>{{'{'}}
 * 	label: 'Cart',
 * 	iconProperty:{{'{'}}
 *  name: 'fa-shopping-cart',
 *  size: '18px',
 *  value: 'buy',
 * {{'}'}},
 * iconPosition: 'right'
 * {{'}'}}
 * </pre>
 */
public properties: ButtonModel;
/**
 * <p> dynamicClass: function() -> string,	// Function that returns name of the class </p>
 * <p> class: string											  // Name of the css class selector </p>
 * <p> themeColor: string									// primary/accent/warn </p>
 * <pre>
 * {{'{'}}
 * 	// Container surrounding the Button
 * 	container:{{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// md-button styling
 * 	button:{{'{'}}
 * 		dynamicClass,
 * 		class,
 * 		themeColor
 * 	{{'}'}},
 * 	// Icon styling: Refer to icon component
 * 	iconStyling: IconStylingModel
 * }
 * </pre>
 */
public styling: ButtonStylingModel;
/**
 * <p> The label for the button</p>
 * @param {?} value
 * @return {?}
 */
set label(value: string) { this.properties.label = value; }
/**
 * <p> Type of the button</p>
 * @param {?} value
 * @return {?}
 */
set type(value: string) { this.properties.type = value; }
/**
 * <p> Collection of needed AM Button visual types like: mat-primary, mat-raised-button, mat-fab, etc.</p>
 * @param {?} value
 * @return {?}
 */
set buttonTheme(value: string) { this.properties.buttonTheme = value; }
/**
 * <p> The name of the icon</p>
 * <p> Refer to icon component</p>
 * @param {?} value
 * @return {?}
 */
set iconName(value: string) {
    if (this.properties.iconProperty == null) { this.properties.iconProperty = new IconModel(); }
    this.properties.iconProperty.name = value;
  }
/**
 * <p> The name of the icon</p>
 * <p> Refer to icon component</p>
 * @param {?} value
 * @return {?}
 */
set iconSize(value: string) {
    if (this.properties.iconProperty == null) { this.properties.iconProperty = new IconModel(); }
    this.properties.iconProperty.size = value;
  }
/**
 * <p> The icon position on the button</p>
 * <p> Options: left or right</p>
 * <p> Default: left</p>
 * @param {?} value
 * @return {?}
 */
set iconPosition(value: string) { this.properties.iconPosition = value; }
/**
 * <p> Display waiting mode (Icon)</p>
 * @param {?} value
 * @return {?}
 */
set wait(value: boolean) { this.properties.waiting.value = value; }
/**
 * @hidden 
 * Is used by CF internal components only to set button theme directly like:
 * 'mat-menu-item' or 'mat-fab' or 'mat-fab-mini'
 * inside corresponding components (cf-menu-item, cf-fab)
 */
_buttonScheme: boolean;
/**
 * <b>Output</b>
 * <p>The event will be emitted when the button is clicked</p>
 */
onClick = new EventEmitter();
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(
public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new ButtonModel();
    this.styling = new ButtonStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    super.ngOnInit();
    if (this.notificationAction.observers.length > 0) { this.notifacationClickable = 'notification-clickable'; }
    this.getMyTemplate('button', ButtonTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'BUTTON').then(sampler => {
        // console.log('BUTTON', this);
      });
    });
  }
/**
 * @hidden 
 * <p>The method will emit event when the button is clicked</p>
 * @return {?}
 */
buttonClicked() { this.onClick.emit(); }
/**
 * @hidden 
 * It is function for creating Notification click events by using Core Component <b>notificationAction</b> event emmiter.
 * @param {?} notification  it is json notification object
 * @return {?}
 */
cfNotificationAction(notification) { this.notificationAction.emit(notification); }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-button',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-button-container'\"> <button md-button type=\"{{properties?.type}}\" [disabled]=\"properties?.disable || (properties?.waiting && properties?.waiting?.value && properties?.waiting?.disabled)\" [ngClass]=\"'cf-button'\" class=\"{{properties?.buttonTheme}} {{_buttonScheme}} {{activeTemplate.style?.button?.class}} {{styling?.button?.class}} {{getDynamicClasses(activeTemplate.style?.button?.dynamicClass)}} {{getDynamicClasses(styling?.button?.dynamicClass)}}\" md-tooltip=\"{{ tooltip ? tooltip.message : tooltip }}\" tooltip-position=\"{{ tooltip?.position }}\" (click)=\"buttonClicked()\" [color]=\"styling?.button?.themeColor || activeTemplate.style?.button?.themeColor\"> <span class=\"cf-button-icon\" *ngIf=\"properties?.iconPosition ==='left' && properties?.iconProperty != null\"> <cf-icon id=\"originalIcon\" *ngIf=\"properties?.iconProperty != null && (!properties?.waiting || (properties?.waiting && !properties?.waiting?.value))\" [properties]=\"properties?.iconProperty\" [styling]=\"styling?.iconStyling\" ></cf-icon> <cf-icon id=\"waitingIcon\" *ngIf=\"properties?.waiting && properties?.waiting?.value && properties?.waiting?.iconProperty\" [properties]=\"properties?.waiting?.iconProperty\" ngClass=\"{{rotate-wait-icon}}\" [styling]=\"styling?.waitingIconStyling\"></cf-icon>       </span> <label>{{ properties?.label }}</label> <div class=\"cf-button-content\"><ng-content></ng-content></div> <span class=\"cf-button-icon\" *ngIf=\"properties?.iconPosition ==='right' && properties?.iconProperty != null\"> <cf-icon id=\"originalIcon\" *ngIf=\"properties?.iconProperty != null && (!properties?.waiting || (properties?.waiting && !properties?.waiting?.value))\" [properties]=\"properties?.iconProperty\" [styling]=\"styling?.iconStyling\" ></cf-icon> <cf-icon id=\"waitingIcon\" *ngIf=\"properties?.waiting && properties?.waiting?.value && properties?.waiting?.iconProperty\" [properties]=\"properties?.waiting?.iconProperty\" ngClass=\"{{rotate-wait-icon}}\" [styling]=\"styling?.waitingIconStyling\"></cf-icon>       </span> </button> <cf-notification *ngIf=\"notification && (notification?.value || notification?.icon)\" [settings]=\"notification\" (click)=\"cfNotificationAction(notification)\"></cf-notification> </div> ",
  styles: ["@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } :host { position: relative; z-index: 0; } :host.notification-clickable cf-notification:hover { cursor: pointer; } :host ::ng-deep button { overflow: hidden; z-index: 0; } :host ::ng-deep button:before { content: ''; position: absolute; z-index: 1; top: 0; right: 0; bottom: 0; left: 0; } :host ::ng-deep button label { cursor: inherit; } :host ::ng-deep button[disabled] label, :host ::ng-deep button[disabled] cf-icon md-icon { color: rgba(0, 0, 0, 0.38) !important; } :host ::ng-deep .cf-button .mat-button-wrapper > * { vertical-align: initial; } :host ::ng-deep .cf-button.mat-menu-item { height: auto; min-height: 48px; border-radius: 0px; } :host ::ng-deep .cf-button.mat-menu-item > .mat-button-wrapper { display: flex; align-items: center; } :host ::ng-deep .cf-button.mat-menu-item > .mat-button-wrapper > .cf-button-icon { margin-right: 16px; } :host ::ng-deep .mat-button-wrapper > label { overflow: hidden; text-overflow: ellipsis; } :host ::ng-deep .cf-button.mat-fab .mat-icon, :host ::ng-deep .cf-button.mat-mini-fab .mat-icon { padding: 0px; } :host ::ng-deep .cf-button-icon { display: inline-flex; align-items: center; } :host ::ng-deep .cf-button-icon .cf-icon { color: inherit; } .cf-submit-button { background-color: green; color: white; } .cancel { margin-left: 20px; } .mat-button { min-width: 50px; } .mat-fab, .mat-mini-fab, .mat-icon-button { min-width: 0px; } div.cf-button-content { vertical-align: initial; display: inline; } :host.rotate-wait-icon ::ng-deep md-icon { display: inline-block; animation: spin 1s infinite linear; cursor: default !important; } .default-container { color: white; } cf-icon ::ng-deep .cf-icon-style { vertical-align: middle; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'notifacationClickable': [{ type: HostBinding, args: ['class', ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'label': [{ type: Input, args: ['label', ] },],
'type': [{ type: Input, args: ['type', ] },],
'buttonTheme': [{ type: Input, args: ['buttonTheme', ] },],
'iconName': [{ type: Input, args: ['iconName', ] },],
'iconSize': [{ type: Input, args: ['iconSize', ] },],
'iconPosition': [{ type: Input, args: ['iconPosition', ] },],
'wait': [{ type: Input, args: ['wait', ] },],
'_buttonScheme': [{ type: Input },],
'onClick': [{ type: Output },],
};
}

function CfButtonComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfButtonComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfButtonComponent.ctorParameters;
/** @type {?} */
CfButtonComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfButtonComponent.prototype.notifacationClickable;
/**
 * <pre>{{'{'}}
 *  display: boolean,         // True or False. Default: True
 *  disable: boolean,         // True or False. Default: False
 *  label: string,            // Default: CF-BUTTON
 *  iconProperty: IconModel,  // Refer to Icon Component
 *  iconPosition: string,     // left, right .Default: left
 *  waiting: {{'{'}}
 *   value: boolean,         // Display waiting mode (Icon)
 *   disable: boolean,       // True or False
 *   iconProperty: IconModel // Refer to Icon Component
 *  {{'}'}}
 * {{'}'}}
 * </pre>
 * <p>Example</p>
 * <pre>{{'{'}}
 * 	label: 'Cart',
 * 	iconProperty:{{'{'}}
 *  name: 'fa-shopping-cart',
 *  size: '18px',
 *  value: 'buy',
 * {{'}'}},
 * iconPosition: 'right'
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfButtonComponent.prototype.properties;
/**
 * <p> dynamicClass: function() -> string,	// Function that returns name of the class </p>
 * <p> class: string											  // Name of the css class selector </p>
 * <p> themeColor: string									// primary/accent/warn </p>
 * <pre>
 * {{'{'}}
 * 	// Container surrounding the Button
 * 	container:{{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// md-button styling
 * 	button:{{'{'}}
 * 		dynamicClass,
 * 		class,
 * 		themeColor
 * 	{{'}'}},
 * 	// Icon styling: Refer to icon component
 * 	iconStyling: IconStylingModel
 * }
 * </pre>
 * @type {?}
 */
CfButtonComponent.prototype.styling;
/**
 * @hidden 
 * Is used by CF internal components only to set button theme directly like:
 * 'mat-menu-item' or 'mat-fab' or 'mat-fab-mini'
 * inside corresponding components (cf-menu-item, cf-fab)
 * @type {?}
 */
CfButtonComponent.prototype._buttonScheme;
/**
 * <b>Output</b>
 * <p>The event will be emitted when the button is clicked</p>
 * @type {?}
 */
CfButtonComponent.prototype.onClick;
/**
 * @hidden
 * @type {?}
 */
CfButtonComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
